import { Box, Button, Grid, Modal, Portal } from '@mui/material';
import { DataGrid, GridToolbar, GridToolbarQuickFilter, GridValueGetter } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import React from 'react';
import { BASE_URL } from '../../api';
import { useTokenStoreStorage, useUserIdStorage } from '../../hooks/useTokenStore';

function MyCustomToolbar(props: any) {
	return (
		<React.Fragment>
			<Portal container={() => document.getElementById('filter-panel')!}>
				<GridToolbarQuickFilter />
			</Portal>
			<GridToolbar {...props} />
		</React.Fragment>
	);
}

export const CustomerTable = () => {
	const [data, setData] = React.useState([]); // Estado para almacenar los datos del servidor
	const [openModal, setOpenModal] = React.useState(false);
	const [selectedRow, setSelectedRow] = React.useState<any>(null); // Especifica el tipo de selectedRow
	const token = useTokenStoreStorage((s) => s.token);
	const userId = useUserIdStorage((s) => s.userId);
	const handleOpenModal = (row: any) => {
		setSelectedRow(row);
		setOpenModal(true);
	};

	const handleCloseModal = () => {
		setOpenModal(false);
	};

	const fetchData = async () => {
		try {
			const response = await axios.get(`${BASE_URL}admin/allCustomer`, {
				headers: {
					AccessToken: token,
					'Content-Type': 'application/json',
				},
				params: {
					user_id: userId,
				},
			});
			return response.data.data.datas;
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	const {
		data: dataset,
		isLoading,
		error,
	} = useQuery({
		queryKey: ['orders'],
		queryFn: fetchData,
	});

	const handleDelete = async () => {
		try {
			await axios.delete(`${BASE_URL}freight/delete`, {
				headers: {
					AccessToken: token,
				},
				params: {
					user_id: userId,
					freight_id: selectedRow.id,
				},
			});
			fetchData();
			setOpenModal(false); // Cerrar modal después de la eliminación exitosa
		} catch (error) {
			console.error('Error deleting data:', error);
		}
	};

	const idValueGetter: GridValueGetter = (value, row) => {
		const id = row.id;

		// Verificar si 'id' existe y no es null
		if (id !== undefined && id !== null) {
			return id;
		} else {
			return 'undefined';
		}
	};
	const usernameValueGetter: GridValueGetter = (value, row) => {
		const username = row.username;

		// Verificar si 'id' existe y no es null
		if (username !== undefined && username !== null) {
			return username;
		} else {
			return 'undefined';
		}
	};
	const emailValueGetter: GridValueGetter = (value, row) => {
		const email = row.email;

		// Verificar si 'id' existe y no es null
		if (email !== undefined && email !== null) {
			return email;
		} else {
			return 'undefined';
		}
	};
	const phoneNumberValueGetter: GridValueGetter = (value, row) => {
		const phoneNumber = row.phoneNumber;

		// Verificar si 'id' existe y no es null
		if (phoneNumber !== undefined && phoneNumber !== null) {
			return phoneNumber;
		} else {
			return 'undefined';
		}
	};
	const fullNameValueGetter: GridValueGetter = (value, row) => {
		const firstname = row.firstname;
		const lastname = row.lastname;

		// Verificar si 'id' existe y no es null
		if (firstname !== undefined && firstname !== null) {
			return firstname + ' ' + lastname;
		} else {
			return 'undefined';
		}
	};
	const bookingQcntValueGetter: GridValueGetter = (value, row) => {
		const BookingQcnt = row.BookingQcnt;

		// Verificar si 'id' existe y no es null
		if (BookingQcnt !== undefined && BookingQcnt !== null) {
			return BookingQcnt;
		} else {
			return 'undefined';
		}
	};
	const last_login_dateValueGetter: GridValueGetter = (value, row) => {
		const last_login_date = row.last_login_date;

		// Verificar si 'id' existe y no es null
		if (last_login_date !== undefined && last_login_date !== null) {
			return last_login_date;
		} else {
			return 'undefined';
		}
	};

	const columns = React.useMemo(
		() => [
			{ field: 'id', flex: 1, headerName: 'ID', width: 70, valueGetter: idValueGetter },
			{ field: 'username', flex: 1, headerName: 'Username', width: 130, valueGetter: usernameValueGetter },
			{ field: 'email', flex: 1, headerName: 'Email', width: 200, valueGetter: emailValueGetter },
			{
				field: 'phoneNumber',
				flex: 1,
				headerName: 'Phone Number',
				width: 130,
				valueGetter: phoneNumberValueGetter,
			},
			{ field: 'fulllname', flex: 1, headerName: 'Full Name', width: 150, valueGetter: fullNameValueGetter },
			{
				field: 'Booking Count',
				flex: 1,
				headerName: 'Booking Count',
				width: 150,
				valueGetter: bookingQcntValueGetter,
			},
			{
				field: 'Last Login',
				flex: 1,
				headerName: 'Last Login',
				width: 170,
				valueGetter: last_login_dateValueGetter,
			},
			{
				field: 'action',
				flex: 1,
				headerName: 'Action',
				renderCell: (params: any) => (
					<Button variant="contained" color="error" onClick={() => handleOpenModal(params.row)}>
						Delete
					</Button>
				),
			},
		],
		[],
	);

	const gridReturn = () => (
		<Grid container spacing={2}>
			<Grid item>
				<Box id="filter-panel" />
			</Grid>
			<Grid item style={{ height: 'auto', width: '100%' }}>
				<DataGrid
					rows={dataset || []}
					columns={columns}
					slots={{ toolbar: MyCustomToolbar }}
					autoHeight={true}
					loading={isLoading}
					initialState={{
						pagination: { paginationModel: { pageSize: 5 } },
					}}
					pageSizeOptions={[5, 10, 25]}
					slotProps={{
						loadingOverlay: {
							variant: 'linear-progress',
							noRowsVariant: 'skeleton',
						},
					}}
				/>
			</Grid>
			<Modal open={openModal} onClose={handleCloseModal}>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 400,
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
					}}
				>
					<p className="mb-6 text-center"> Are You sure to DELETE?</p>
					<div className="flex justify-between">
						<Button onClick={handleDelete} color="error" variant="contained" sx={{ marginRight: 3 }}>
							Delete
						</Button>
						<Button onClick={handleCloseModal} color="primary" variant="contained">
							Cancel
						</Button>
					</div>
				</Box>
			</Modal>
		</Grid>
	);

	return (
		<>
			<h1 className="m-3">Admin customers</h1>
			<div className="m-10">{gridReturn()}</div>
		</>
	);
};
